import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import Modal from "../Modal";
import Button from "../Button";
import { OPERATOR_LABELS } from "./ColumnMenu";
import {
  TEXT_OPERATORS as DEFAULT_OPERATORS,
  IMMEDIATE_OPERATORS,
  LIST_OPERATORS,
  RANGE_OPERATORS,
} from "./helpers/filterOperators";

// The column filter submenu. Value-requiring operators open a labeled modal;
// In List / Not in List parse comma-separated values; Between takes two inputs;
// Is Empty / Is Not Empty apply immediately. Writes the column filter slot via
// column.setFilter({ operator, values }). Rendered inside <ColumnMenu>, which
// passes `editToken` (pencil → re-open prefilled) and `onEditingChange` (so the
// menu keeps the panel/modal mounted while the modal is open).
//
// `operators` and `inputType` are supplied per column by <DataTable> from the
// column's inferred type, so numeric columns get Greater Than / Between with a
// number input and date columns get Before / After with a date picker. Defaults
// are the text set, so callers that pass neither are unaffected.

const FilterLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  padding: 0 0.4rem 0.25rem;
`;

const OpList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OpButton = styled.button`
  text-align: left;
  background: ${(p) => (p.active ? "#eff6ff" : "none")};
  color: ${(p) => (p.active ? "#2563eb" : "inherit")};
  border: none;
  cursor: pointer;
  padding: 0.3rem 0.4rem;
  font-size: 0.85rem;
  border-radius: 4px;
  &:hover {
    background: #f5f5f5;
  }
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

// Autocomplete dropdown shown under the value input once the active term is at
// least MIN_SUGGEST_CHARS long (FR: distinct-value suggestions).
const SuggestList = styled.ul`
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #d1d5db;
  border-radius: 4px;
`;

const SuggestItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.4rem 0.5rem;
  font-size: 0.9rem;
  &:hover {
    background: #eff6ff;
  }
`;

const MIN_SUGGEST_CHARS = 2;
const SUGGEST_DEBOUNCE_MS = 250;

const TextColumnFilter = ({
  column,
  editToken = 0,
  onEditingChange,
  onClose,
  operators,
  suggest,
  inputType = "text",
}) => {
  const [modalOp, setModalOp] = useState(null);
  const [inputValue, setInputValue] = useState("");
  // Second bound, used only by range operators (Between).
  const [inputValue2, setInputValue2] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const ops = operators ?? DEFAULT_OPERATORS;
  const headerLabel = typeof column.Header === "string" ? column.Header : "";

  const isRangeOp = RANGE_OPERATORS.includes(modalOp);

  // For list operators the value is comma-separated; autocomplete keys off the
  // token after the last comma. Single-value operators match the whole input.
  const isListOp = LIST_OPERATORS.includes(modalOp);
  const activeTerm = isListOp
    ? inputValue.split(",").pop().trim()
    : inputValue.trim();

  // Debounced distinct-value lookup. A sequence guard drops stale responses so
  // fast typing can't render an out-of-order suggestion list. Suppressed for
  // number/date columns — distinct-value autocomplete is a text affordance and
  // the native pickers already constrain input.
  const suggestSeq = useRef(0);
  useEffect(() => {
    if (
      !suggest ||
      inputType !== "text" ||
      !modalOp ||
      activeTerm.length < MIN_SUGGEST_CHARS
    ) {
      setSuggestions([]);
      return undefined;
    }
    const seq = ++suggestSeq.current;
    const timer = setTimeout(async () => {
      try {
        const results = await suggest(activeTerm);
        if (seq === suggestSeq.current) setSuggestions(results ?? []);
      } catch {
        if (seq === suggestSeq.current) setSuggestions([]);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [activeTerm, modalOp, suggest, inputType]);

  const pickSuggestion = (value) => {
    if (isListOp) {
      const parts = inputValue.split(",");
      parts[parts.length - 1] = ` ${value}`;
      setInputValue(`${parts.join(",")}, `);
    } else {
      setInputValue(value);
    }
    setSuggestions([]);
  };

  const openEditor = (operator, prefill) => {
    const values = prefill ?? [];
    setModalOp(operator);
    // Range operators keep their bounds in separate inputs; every other
    // operator shows a single (comma-joined for lists) value.
    if (RANGE_OPERATORS.includes(operator)) {
      setInputValue(values[0] ?? "");
      setInputValue2(values[1] ?? "");
    } else {
      setInputValue(values.join(", "));
      setInputValue2("");
    }
    if (onEditingChange) onEditingChange(true);
  };

  const closeModal = () => {
    setModalOp(null);
    setInputValue2("");
    setSuggestions([]);
    if (onEditingChange) onEditingChange(false);
    // Dismiss the parent column menu too — once a filter is applied or the
    // modal is cancelled there's nothing left to do in the panel.
    if (onClose) onClose();
  };

  const choose = (operator) => {
    if (IMMEDIATE_OPERATORS.includes(operator)) {
      column.setFilter({ operator, values: [] });
      if (onClose) onClose();
      return;
    }
    const prefill =
      column.filterValue?.operator === operator
        ? column.filterValue.values
        : [];
    openEditor(operator, prefill);
  };

  // Pencil edit (from the active-filter row): re-open the current filter
  // prefilled. Skip the initial render (editToken starts at 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const fv = column.filterValue;
    if (fv && !IMMEDIATE_OPERATORS.includes(fv.operator)) {
      openEditor(fv.operator, fv.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editToken]);

  const submit = () => {
    let values;
    if (LIST_OPERATORS.includes(modalOp)) {
      values = inputValue
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (RANGE_OPERATORS.includes(modalOp)) {
      const from = inputValue.trim();
      const to = inputValue2.trim();
      // Both bounds are required — a half-open Between would silently behave
      // like a one-sided comparison.
      values = from !== "" && to !== "" ? [from, to] : [];
    } else {
      const trimmed = inputValue.trim();
      values = trimmed === "" ? [] : [trimmed];
    }
    // An empty value clears the filter rather than applying a no-op.
    column.setFilter(values.length ? { operator: modalOp, values } : undefined);
    closeModal();
  };

  const activeOp = column.filterValue?.operator;

  return (
    <>
      <FilterLabel>Filter</FilterLabel>
      <OpList>
        {ops.map((op) => (
          <OpButton
            key={op}
            type="button"
            active={activeOp === op}
            onClick={() => choose(op)}
          >
            {OPERATOR_LABELS[op]}
          </OpButton>
        ))}
      </OpList>
      {modalOp ? (
        <Modal
          title={`Show records where: ${headerLabel} ${OPERATOR_LABELS[modalOp]}`}
          hide={closeModal}
          noWidth
        >
          <div style={{ minWidth: "320px" }}>
            <label style={{ fontSize: "0.85rem" }}>
              {LIST_OPERATORS.includes(modalOp)
                ? "Values (comma-separated)"
                : isRangeOp
                ? "From"
                : "Value"}
              <ModalInput
                autoFocus
                // Lists stay free text even on a typed column — the value is a
                // comma-separated string, which type="number"/"date" reject.
                type={LIST_OPERATORS.includes(modalOp) ? "text" : inputType}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
              />
            </label>
            {isRangeOp ? (
              <label style={{ fontSize: "0.85rem" }}>
                To
                <ModalInput
                  type={inputType}
                  value={inputValue2}
                  onChange={(e) => setInputValue2(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submit();
                  }}
                />
              </label>
            ) : null}
            {suggestions.length > 0 ? (
              <SuggestList role="listbox">
                {suggestions.map((s) => (
                  <li key={s}>
                    <SuggestItem
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickSuggestion(s)}
                    >
                      {s}
                    </SuggestItem>
                  </li>
                ))}
              </SuggestList>
            ) : null}
            <div style={{ marginTop: "1rem" }}>
              <Button type="button" list="true" onClick={submit}>
                Apply
              </Button>
              <Button type="button" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default TextColumnFilter;
